import {
  Component,
  ChangeDetectionStrategy,
  input,
  inject,
  DestroyRef,
  signal,
  OnInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { TeacherProfile } from "../../../api/models/teacher-profile";
import { StudentProfile } from "../../../api/models/student-profile";
import { SchoolClass } from "../../../api/models/school-class";
import { SchoolService } from "../../../api/services/school.service";
import { LoaderComponent } from "../../../core/loader/loader.component";

@Component({
  selector: "app-teacher-students-tab",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    FontAwesomeModule,
    LoaderComponent,
  ],
  template: `
    <div>
      <div class="card mb-3">
        <div class="card-body">
          <h5 class="card-title">{{ "My students" | translate }}</h5>

          <div class="row mb-3">
            @if (schoolClasses().length > 1) {
            <div class="col-12 col-sm-6">
              <select
                class="form-control"
                [ngModel]="selectedClassId"
                (ngModelChange)="onClassChange($event)"
              >
                @for (cls of schoolClasses(); track cls.id) {
                <option [value]="cls.id">{{ cls.name }}</option>
                }
              </select>
            </div>
            }
          </div>

          @if (studentsLoading()) {
          <app-loader></app-loader>
          } @if (!studentsLoading() && students().length === 0) {
          <div class="text-center text-muted py-4">
            <p>{{ "No students found" | translate }}</p>
          </div>
          } @if (!studentsLoading()) {
          <div class="row">
            @for (student of students(); track student.id ?? $index) {
            <div class="col-12 col-sm-6 col-md-4 mb-3">
              <div class="card h-100">
                <div class="card-body text-center">
                  <div class="mb-2">
                    @if (student.avatarUri) {
                    <img
                      [src]="student.avatarUri"
                      [alt]="student.firstName + ' ' + student.lastName"
                      class="rounded-circle"
                      width="60"
                      height="60"
                    />
                    } @else {
                    <div
                      class="rounded-circle bg-secondary d-inline-flex align-items-center justify-content-center"
                      style="width: 60px; height: 60px;"
                    >
                      <fa-icon [icon]="faUser" class="text-white"></fa-icon>
                    </div>
                    }
                  </div>
                  <h6 class="card-title mb-1">
                    {{ student.firstName }} {{ student.lastName }}
                  </h6>
                  @if (student.homeAddress.city) {
                  <small class="text-muted">{{
                    student.homeAddress.city
                  }}</small>
                  }
                </div>
              </div>
            </div>
            }
          </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class TeacherStudentsTabComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly schoolService = inject(SchoolService);

  profile = input.required<TeacherProfile>();
  schoolClasses = input.required<SchoolClass[]>();

  readonly students = signal<StudentProfile[]>([]);
  readonly studentsLoading = signal(false);
  selectedClassId = "";

  faUser = faUser;

  ngOnInit() {
    const classes = this.schoolClasses();
    if (classes.length > 0) {
      this.selectedClassId = classes[0].id || "";
      this.loadStudentsForClass(classes[0]);
    }
  }

  onClassChange(classId: string) {
    this.selectedClassId = classId;
    const cls = this.schoolClasses().find((c) => c.id === classId);
    if (cls) this.loadStudentsForClass(cls);
  }

  private loadStudentsForClass(cls: SchoolClass) {
    if (!cls.id) return;
    const school = this.profile()?.schools?.[0];
    if (!school?.id) return;

    this.studentsLoading.set(true);
    this.schoolService
      .getStudentsInClass({ schoolId: school.id, id: cls.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          this.students.set(
            Array.isArray(result) ? result : result?.data || []
          );
          this.studentsLoading.set(false);
        },
        error: () => {
          this.students.set([]);
          this.studentsLoading.set(false);
        },
      });
  }
}
